import React, { useRef, useEffect, useState, memo } from 'react';
import { Paper } from 'components';
import { IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Fullscreen, FullscreenExit } from '@mui/icons-material';
// Style import
import useStyles from './styles';
import useWindowSize from 'hooks/useWindowSize';

// Detect iOS

const useFullScreenStyles = makeStyles((theme) => ({
  videoRoot: {
    marginTop: theme.spacing(1.4),
    marginRight: theme.spacing(2.0)
  },
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '96vh', // Will be overridden by inline styles for iOS
    backgroundColor: '#000',
    background: 'rgba(0, 0, 0, 0.9)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 0.3s ease-in-out',
    opacity: 1,
    visibility: 'visible',
    // iOS specific optimizations
    WebkitTransform: 'translate3d(0,0,0)',
    transform: 'translate3d(0,0,0)',
    WebkitOverflowScrolling: 'touch',
    // Prevent iOS bounce effect
    overflow: 'hidden',
    touchAction: 'none'
  },

  video: {
    width: '100%',
    height: '100%',
    objectFit: 'contain', // Changed from 'fill' to 'contain' for better iOS compatibility
    zIndex: 1000,
    backgroundColor: '#000',
    // iOS specific video optimizations
    WebkitPlaysinline: true,
    playsinline: true,
    WebkitTransform: 'translateZ(0)',
    transform: 'translateZ(0)'
  },

  toggleButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 10000,
    color: 'white',
    background: 'rgba(0,0,0,0.8)',
    borderRadius: 20,

    '&:hover': {
      background: 'rgba(0,0,0,0.9)'
    },
    '&:active': {
      background: 'rgba(0,0,0,0.9)'
    }
  }
}));

const ScreenShare = ({
  screenShareTrack,
  istTutor,
  setIsFullScreen,
  isFullScreen,
  fullScreenHeight
}) => {
  const screenShareRef = useRef(null);
  const classes = useStyles();
  const fullScreenClasses = useFullScreenStyles();
  const [width] = useWindowSize();

  const toggleFullScreen = () => {
    setIsFullScreen((prev) => !prev);
  };
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'F11') {
        event.preventDefault();
        toggleFullScreen();
      }
      // Also handle Escape key to exit fullscreen
      if (event.key === 'Escape' && isFullScreen) {
        setIsFullScreen(false);
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyPress);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isFullScreen, setIsFullScreen]);

  // Prevent body scroll when in fullscreen
  useEffect(() => {
    if (isFullScreen) {
      // Store original overflow style
      const originalOverflow = document.body.style.overflow;
      // Prevent scrolling
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore original overflow when component unmounts or exits fullscreen
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isFullScreen]);

  // iOS-specific fullscreen container styles
  const getFullscreenContainerStyle = () => {
    if (!isFullScreen) return {};

    return {
      height: width < 900 && width > 500 ? fullScreenHeight ?? '90vh' : '99vh',
      // Additional iOS fixes
      WebkitTransform: 'translate3d(0,0,0)',
      transform: 'translate3d(0,0,0)',
      backfaceVisibility: 'hidden',
      perspective: 1000
    };
  };

  // iOS-specific video styles
  const getVideoStyle = () => {
    if (!isFullScreen) return {};

    return {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      // iOS video rendering optimization
      WebkitTransform: 'translateZ(0)',
      transform: 'translateZ(0)',
      // Ensure video takes full space on iOS
      maxWidth: '100%',
      maxHeight: '100%'
    };
  };

  const containerStyle = {
    width: isFullScreen
      ? '100%'
      : width < 500
      ? 230
      : width >= 500 && width <= 900
      ? 250
      : 310,
    height: isFullScreen
      ? '96vh'
      : width < 500
      ? 75
      : width >= 500 && width <= 900
      ? 45
      : 175,
    position: isFullScreen ? 'fixed' : 'relative',
    top: isFullScreen ? 0 : 'auto',
    left: isFullScreen ? 0 : 'auto',
    zIndex: 1000,
    backgroundColor: isFullScreen ? '#000' : 'transparent',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 0.3s ease-in-out',

    ...getFullscreenContainerStyle()
  };

  // detuct if the device is iOS
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  };
  const widthandHeightStyle =
    width >= 500 && width <= 900 ? { width: '30px', height: '30px' } : {};

  const widthandHeightofIconStyle =
    width >= 500 && width <= 900 ? { width: '20px', height: '20px' } : {};
  return (
    <Paper
      style={containerStyle}
      className={isFullScreen ? '' : fullScreenClasses.videoRoot}>
      <video
        ref={(ref) => ref && screenShareTrack.attach(ref)}
        className={isFullScreen ? fullScreenClasses.video : classes.video}
        style={{
          ...getVideoStyle(),
          ...(isIOS() ? { zIndex: -1 } : { zIndex: 1 }) // Add zIndex -1 for iOS
        }}
        autoPlay
        playsInline
        muted // Add muted for iOS autoplay compatibility
        webkit-playsinline="true" // Explicit iOS attribute
      />
      {!istTutor && (
        <div
          style={{
            position: 'absolute',
            top: 5,
            right: 2,
            zIndex: 10001
          }}>
          <IconButton
            className={fullScreenClasses.toggleButton}
            onClick={toggleFullScreen}
            style={{
              ...widthandHeightStyle,
              touchAction: 'manipulation'
            }}>
            {isFullScreen ? (
              <FullscreenExit style={widthandHeightofIconStyle} />
            ) : (
              <Fullscreen style={widthandHeightofIconStyle} />
            )}
          </IconButton>
        </div>
      )}
    </Paper>
  );
};
export default memo(ScreenShare);
